<template>
  <div class="sbe">
    <div v-if="value.length" class="sbe__rail" aria-label="Range preview">
      <span
        v-for="(band, i) in value"
        :key="`rail-${i}`"
        class="sbe__rail-segment"
        :style="{ backgroundColor: paintSwatch(band) }">
        <span class="sbe__rail-label">{{ band.label || `Range ${i + 1}` }}</span>
        <span class="sbe__rail-range">{{ rangeLabel(i) }}</span>
      </span>
    </div>

    <p class="sbe__guidance">
      Ranges stay connected: each one begins where the previous one ends.
    </p>

    <div class="sbe__bands">
      <div v-for="(band, i) in value" :key="i" class="sbe-band">
        <span class="sbe-band__order" :aria-label="`Range ${i + 1}`">{{ i + 1 }}</span>

        <div class="sbe-band__range">
          <span class="sbe-band__field-label">Range</span>
          <div class="sbe-band__bounds">
            <span class="sbe-band__bound">{{ i === 0 ? 'No lower limit' : fromDisplay(i) }}</span>
            <v-icon small class="sbe-band__arrow">mdi-arrow-right</v-icon>
            <v-text-field
              v-if="i < value.length - 1"
              :value="band.to"
              type="number"
              placeholder="Boundary"
              outlined
              dense
              hide-details
              :aria-label="`Upper boundary for range ${i + 1}`"
              class="sbe-band__to"
              @change="setTo(i, $event)">
            </v-text-field>
            <span v-else class="sbe-band__bound">No upper limit</span>
          </div>
        </div>

        <div class="sbe-band__label">
          <span class="sbe-band__field-label">Label</span>
          <v-text-field
            :value="band.label"
            placeholder="Describe this range"
            maxlength="24"
            outlined
            dense
            hide-details
            :aria-label="`Label for range ${i + 1}`"
            @change="setLabel(i, $event)">
          </v-text-field>
        </div>

        <div class="sbe-band__paint">
          <span class="sbe-band__field-label">Color</span>
          <schema-color-field
            :value="bandColorPair(band)"
            :aria-label="`Choose color for range ${i + 1}`"
            hint="Choose any color for this range."
            @input="setColor(i, $event)">
          </schema-color-field>
        </div>

        <v-btn
          icon
          small
          class="sbe-band__remove"
          :disabled="value.length <= 1"
          :title="value.length <= 1 ? 'A schema needs at least one range' : 'Remove range'"
          @click="removeBand(i)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="sbe__actions">
      <v-btn text small color="primary" @click="addBand">
        <v-icon small left>mdi-plus</v-icon> Add range
      </v-btn>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            small
            class="sbe__reverse"
            aria-label="Reverse range colors"
            @click="reversePaints">
            <v-icon small>mdi-swap-horizontal</v-icon>
          </v-btn>
        </template>
        <span>Reverse range colors</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { TONE, TONE_SWATCH, NEUTRAL_SWATCH, paintSwatch } from '@/features/Dashboard/utils/coloringSchemas';
import { numOrNull } from '@/features/Dashboard/utils/numberCoerce';
import SchemaColorField from './SchemaColorField.vue';

export default {
  name: 'SchemaBandsEditor',

  components: { SchemaColorField },

  props: {
    value: { type: Array, default: () => [] },
  },

  methods: {
    fromDisplay(i) {
      const previous = this.value[i - 1];
      const to = previous ? previous.to : null;
      return (to === null || to === undefined || to === '') ? 'Set above' : String(to);
    },

    rangeLabel(i) {
      const band = this.value[i] || {};
      const from = i === 0 ? 'Below' : this.fromDisplay(i);
      const to = i === this.value.length - 1 ? 'and above' : band.to || 'set boundary';
      return `${from} ${to}`;
    },

    // Shared editor-preview swatch resolver (tone swatch | raw color | neutral).
    paintSwatch,

    // The wrapper picker authors a {light, dark} pair; surface a tone band as its swatch so the
    // trigger shows the current color, and picking a custom hue converts the band to a raw color.
    bandColorPair(band) {
      if (band && band.color && band.color.light) return band.color;
      if (band && band.tone) {
        const hex = TONE_SWATCH[band.tone] || NEUTRAL_SWATCH;
        return { light: hex, dark: hex };
      }
      return { light: NEUTRAL_SWATCH, dark: NEUTRAL_SWATCH };
    },

    emitBands(bands) {
      this.$emit('input', bands);
    },

    addBand() {
      this.emitBands([...this.value, { to: null, tone: TONE.CAUTION, color: null, label: '' }]);
    },

    removeBand(i) {
      if (this.value.length <= 1) return;
      this.emitBands(this.value.filter((_, index) => index !== i));
    },

    reversePaints() {
      const paints = this.value.map(band => ({ tone: band.tone || null, color: band.color || null })).reverse();
      this.emitBands(this.value.map((band, i) => ({ ...band, ...paints[i] })));
    },

    patchBand(i, patch) {
      this.emitBands(this.value.map((band, index) => (index === i ? { ...band, ...patch } : band)));
    },

    setTo(i, value) {
      this.patchBand(i, { to: numOrNull(value) });
    },

    setLabel(i, value) {
      this.patchBand(i, { label: value });
    },

    setColor(i, pair) {
      this.patchBand(i, { tone: null, color: pair });
    },
  },
};
</script>

<style lang="scss" scoped>
.sbe { display: flex; flex-direction: column; gap: 12px; }
.sbe__rail { display: flex; min-height: 58px; overflow: hidden; border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08)); border-radius: var(--ds-radius-md, 8px); }
.sbe__rail-segment { position: relative; display: flex; flex: 1 1 0; flex-direction: column; justify-content: center; min-width: 0; padding: 9px 11px; color: #fff; }
.sbe__rail-segment + .sbe__rail-segment::before { position: absolute; top: 0; bottom: 0; left: 0; width: 1px; background: rgba(255, 255, 255, 0.62); content: ''; }
.sbe__rail-label, .sbe__rail-range { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sbe__rail-label { font-size: 12px; font-weight: 700; }
.sbe__rail-range { margin-top: 2px; font-size: 10.5px; opacity: 0.9; }
.sbe__guidance { margin: -2px 0 0; color: #52615c; font-size: 12.5px; font-weight: 500; line-height: 1.45; }
.sbe__bands { overflow: hidden; border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08)); border-radius: var(--ds-radius-md, 8px); }
.sbe-band { display: grid; grid-template-columns: 26px minmax(180px, 1.25fr) minmax(150px, 1fr) minmax(146px, .9fr) 28px; gap: 12px; align-items: end; padding: 12px; }
.sbe-band + .sbe-band { border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08)); }
.sbe-band__order { display: grid; width: 25px; height: 25px; place-items: center; margin-bottom: 7px; border: 1px solid #b9cbc4; border-radius: 50%; color: #3f564d; font-size: 12px; font-weight: 700; }
.sbe-band__field-label { display: block; margin-bottom: 6px; color: #52615c; font-size: 11px; font-weight: 700; letter-spacing: .045em; text-transform: uppercase; }
.sbe-band__bounds { display: flex; align-items: center; height: 40px; gap: 6px; }
.sbe-band__bound { min-width: 92px; height: 40px; display: inline-flex; align-items: center; justify-content: center; padding: 0 8px; border-radius: var(--ds-radius-sm, 4px); background: var(--ds-surface-hover, rgba(3, 98, 76, 0.04)); color: #52615c; font-size: 12.5px; font-weight: 500; text-align: center; white-space: nowrap; }
.sbe-band__arrow { color: #52615c; opacity: .82; }
.sbe-band__to { max-width: 96px; }
.sbe-band__to ::v-deep .v-input__slot { min-height: 40px !important; margin-bottom: 0 !important; }
.sbe-band__to ::v-deep input { text-align: center; }
.sbe-band__remove { margin-bottom: 4px; color: #52615c; }
.sbe-band__remove:hover:not(.v-btn--disabled) { color: var(--v-error-base); background: rgba(183, 28, 28, 0.08); }
.sbe-band__remove:focus-visible { outline: 2px solid var(--ds-focus-ring-color, #2cc295); outline-offset: 2px; }
.sbe__actions { display: flex; align-items: center; min-height: 36px; }
.sbe__actions .v-btn { min-height: 36px; padding: 0 10px; font-size: 13px; font-weight: 600; text-transform: none; letter-spacing: normal; }
.sbe__actions .v-btn:focus-visible { outline: 2px solid var(--ds-focus-ring-color, #2cc295); outline-offset: 2px; }
.sbe__reverse { flex: 0 0 auto; width: 36px; height: 36px; min-width: 36px !important; padding: 0 !important; border: 1px solid var(--ds-border-default, rgba(24, 49, 44, 0.22)); border-radius: var(--ds-radius-sm, 4px); color: #3f564d; }
.sbe__reverse:hover:not(.v-btn--disabled) { border-color: rgba(3, 98, 76, 0.45); background: rgba(3, 98, 76, 0.08); color: var(--v-primary-base); }
.theme--dark .sbe__bands, .theme--dark .sbe__rail, .theme--dark .sbe-band + .sbe-band { border-color: rgba(255, 255, 255, 0.1); }
.theme--dark .sbe-band__bound { background: rgba(255, 255, 255, 0.06); }
.theme--dark .sbe__guidance, .theme--dark .sbe-band__field-label, .theme--dark .sbe-band__bound, .theme--dark .sbe-band__arrow, .theme--dark .sbe-band__remove, .theme--dark .sbe__reverse { color: rgba(255, 255, 255, 0.78); }
@media (max-width: 720px) { .sbe-band { grid-template-columns: 26px 1fr 28px; gap: 10px; } .sbe-band__range, .sbe-band__label, .sbe-band__paint { grid-column: 2; } .sbe-band__remove { grid-column: 3; grid-row: 1; } .sbe__rail { overflow-x: auto; } .sbe__rail-segment { min-width: 120px; } }
</style>
